import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { DialogFooter } from "@/components/ui/dialog"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { formSchema } from "./send-recipient.schema"
import Separator from "@/components/ux/separator"
import {
  CheckIcon,
  MagnifyingGlassIcon,
  ReloadIcon,
} from "@radix-ui/react-icons"
import { useEffect, useState } from "react"
import { useUserSearch } from "@/api/users"

interface SendRecipientFormProps {
  onSubmit: (data: z.infer<typeof formSchema>) => void;
}

export default function SendRecipientForm({
  onSubmit,
}: SendRecipientFormProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const { user, isLoading, isUnknown } = useUserSearch(searchQuery)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      address: "",
    },
  })

  useEffect(() => {
    if (user && user.wallet) {
      form.setValue("wallet", user.wallet)
    } else {
      form.setValue("wallet", undefined)
    }
  }, [user, form])

  return (
    <Form {...form}>
      <p className="text-gray-300 text-sm">
        Search for a BraidPay account or enter a wallet address:
      </p>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4 py-4">
          <FormItem>
            <FormLabel>Send to a BraidPay account</FormLabel>
            <FormControl>
              <Input
                className={isUnknown ? "border-red-500" : ""}
                leftIcon={
                  isLoading ? (
                    <ReloadIcon
                      width={20}
                      height={20}
                      className="animate-spin mt-[2px]"
                    />
                  ) : user ? (
                    <CheckIcon width={24} height={24} />
                  ) : (
                    <MagnifyingGlassIcon width={24} height={24} />
                  )
                }
                placeholder="Email"
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </FormControl>
          </FormItem>
          <Separator>OR</Separator>
          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Wallet Address</FormLabel>
                <FormControl>
                  <Input placeholder="Wallet Address" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <DialogFooter>
          <Button type="submit">Next</Button>
        </DialogFooter>
      </form>
    </Form>
  )
}
